import { ExtensionFirebaseSchema } from "../@types/sharedSchema";

/**
 * The account's WhatsApp-extension status, worked out from what its tabs report (protocol v2). Pure — no
 * Firebase — so it can be tested and recomputed as leases age.
 */

/**
 * A tab whose lease is older than this is not alive. Extensions from 1.0.93 renew it every 5 minutes (a
 * background tab's timer can run a minute late) and at once on any change; older ones every 5 s, or once
 * a minute in a background tab.
 */
export const LIVE_LEASE_MS = 12 * 60_000;
/** Before 1.0.93 a tab has no `state` and heartbeats only while WhatsApp is ready, at least once a minute. */
export const LEGACY_READY_LEASE_MS = 3 * 60_000;

type TabState = ExtensionFirebaseSchema.ExtensionTabState;

/** What the account's WhatsApp tabs can do right now, from what they last reported. */
export type ExtensionPresence = {
  /** The best state among the account's live tabs; "none" when no tab reported in the live window. */
  state: TabState | "none";
  /** The tab to address jobs to (extensions before 1.0.93 only take jobs addressed to them). */
  activeGuid: string | null;
  /**
   * That tab runs an extension older than 1.0.93. Such a tab sends a job even after it was cancelled (it
   * ignores a refused claim, e.g. when it wakes from sleep), and does not look in the chat before a
   * resend: a job it did not take is `unknown`, never queued again on its own.
   */
  legacy: boolean;
  /** When the most recently heard-from tab last reported (ms), or null. */
  lastSeenAt: number | null;
  /** WhatsApp's raw values from the best tab, for support. */
  reason?: string;
  /** The WhatsApp number of the best tab (1.0.93+). */
  waUser?: string | null;
};

/**
 * How a job ended:
 *  - success / failed: the extension answered.
 *  - not_picked_up: no tab took it in time; it was cancelled, nothing was sent → safe to queue again.
 *  - not_ready: a tab took it but WhatsApp was not ready; nothing was sent → safe to queue again.
 *  - unknown: a tab took it but did not answer in time — or a tab older than 1.0.93 did not take it, and
 *    may still send it when it wakes up; it MAY have been sent → never queue it again without checking
 *    (see `dedupeSince`; an older extension does not check).
 */
export type ExtensionOutcome = "success" | "failed" | "not_picked_up" | "not_ready" | "unknown";

/** Nothing was sent: queuing the same message again cannot send it twice. */
export const isSafeToRetry = (outcome: ExtensionOutcome) => outcome === "not_picked_up" || outcome === "not_ready";

const STATE_RANK: Record<TabState, number> = {
  ready: 6,
  loading: 5,
  conflict: 4,
  logged_out: 3,
  offline: 2,
  asleep: 1,
  closed: 0
};

export const toMillis = (value: unknown): number | null => {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  const maybe = value as { toMillis?: () => number; seconds?: number };
  if (typeof maybe.toMillis === "function") return maybe.toMillis();
  return typeof maybe.seconds === "number" ? maybe.seconds * 1000 : null;
};

/**
 * The account's status from its tabs' listener documents. Never routes by creation time: the
 * newest-created tab is often a dead one (measured on 9% of live accounts, 2026-10-24) while an older tab
 * is alive.
 */
export function presenceFromListeners(
  listeners: Array<Partial<ExtensionFirebaseSchema.WhatsappExtensionListener> & { id: string }>,
  now: number
): ExtensionPresence {
  let best: {
    id: string;
    state: TabState;
    legacy: boolean;
    leaseAt: number;
    reason?: string;
    waUser?: string | null;
  } | null = null;
  let lastSeenAt: number | null = null;

  for (const listener of listeners) {
    const leaseAt = toMillis(listener.forcedUpdatedTime);
    if (!leaseAt) continue;
    lastSeenAt = Math.max(lastSeenAt ?? 0, leaseAt);
    const age = now - leaseAt;
    if (age > LIVE_LEASE_MS) continue;
    // Before 1.0.93 there is no state: a fresh heartbeat means ready, a stale one means nothing.
    const state: TabState | null = listener.state ?? (age <= LEGACY_READY_LEASE_MS ? "ready" : null);
    if (!state || state === "closed") continue;
    const legacy = !listener.state;
    // At the same state a 1.0.93+ tab wins over an older one, which heartbeats far more often: a 1.0.93+
    // tab also takes jobs addressed to an older tab after a grace, and both taking one sends it twice.
    const better =
      !best ||
      STATE_RANK[state] > STATE_RANK[best.state] ||
      (STATE_RANK[state] === STATE_RANK[best.state] &&
        (legacy !== best.legacy ? !legacy : leaseAt > best.leaseAt));
    if (better) best = { id: listener.id, state, legacy, leaseAt, reason: listener.stateReason, waUser: listener.waUser };
  }

  if (!best) return { state: "none", activeGuid: null, legacy: false, lastSeenAt };
  return {
    state: best.state,
    activeGuid: best.id,
    legacy: best.legacy,
    lastSeenAt,
    reason: best.reason,
    waUser: best.waUser
  };
}

/** What the realtor should see for a status, in plain Hebrew with what to do. */
export function describePresence(presence: ExtensionPresence | null): string {
  switch (presence?.state) {
    case "ready":
      return "החיבור תקין";
    case "loading":
      return "WhatsApp Web נטען…";
    case "logged_out":
      return "WhatsApp Web לא מחובר: סרקו את קוד ה-QR בלשונית של WhatsApp Web";
    case "conflict":
      return 'WhatsApp פתוח בחלון אחר: לחצו "Use here" (שימוש כאן) בלשונית של WhatsApp Web';
    case "offline":
      return "WhatsApp Web מנסה להתחבר: בדקו את חיבור האינטרנט במחשב שבו הוא פתוח";
    case "asleep":
      return "הדפדפן הרדים את לשונית WhatsApp Web: לחצו עליה פעם אחת כדי להעיר אותה";
    default:
      return "לא נמצאה לשונית WhatsApp Web פתוחה עם התוסף: פתחו את web.whatsapp.com";
  }
}
